import { useCallback, useEffect, useState, useRef, forwardRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SelectModal } from "./ManageChannelsStyles";
import RadioInput from "components/global/RadioInput";
import Loader from "components/global/Loader";
import ImgWrapper from "components/global/ImgWrapper";
import Spacer from "components/global/Spacer";
import { axiosInstance } from "api";
import { showAlert } from "utils/utils";
import userIcon from "assets/icons/userIconWhite.svg";
import { socialIcons } from "./ManageChannels";
import Info from "components/global/Info";

const FacebookContainer = forwardRef(
  ({ item, selectedAccountId, handleSelectAccount }, ref) => {
    const [profilePic, setProfilePic] = useState("");

    const getProfilePic = async () => {
      try {
        const res = await axiosInstance.get(
          `https://graph.facebook.com/v17.0/${item?.id}?fields=picture&transport=cors&access_token=${item?.access_token}`
        );
        setProfilePic(res.data?.picture?.data?.url);
      } catch (error) {
        console.log(error);
      }
    };

    useEffect(() => {
      getProfilePic();
      // eslint-disable-next-line
    }, []);

    return (
      <>
        <div
          className={`flexRow alignCenter chooseTargetItem ${
            item.exists && "disabled"
          }`}
          ref={ref}
        >
          <RadioInput
            id={`_${item.id}`}
            name="facebook_page"
            value={item.id}
            checked={selectedAccountId === item.id}
            onClick={() => handleSelectAccount(item.id, profilePic)}
            disabled={item.exists}
          />
          <Spacer x={1.2} />
          <label htmlFor={`_${item.id}`} className="flexRow alignCenter">
            <div className="user-image-container">
              <ImgWrapper size={4.0} imgHeight={"100%"}>
                <img src={item.profile_picture || userIcon} alt="avi" />
              </ImgWrapper>
              <div className="social-icon flexRow alignCenter justifyCenter">
                <img
                  src={socialIcons.circle["facebook"]}
                  alt="text"
                  width="16px"
                />
              </div>
            </div>
            <Spacer x={1.2} />
            <span className="text l3 colorBodyText">{item.name}</span>
          </label>
        </div>
        {item?.exists && (
          <Info
            content={`Page connected to ${item.owner} workspace on PostPaddy`}
          />
        )}
      </>
    );
  }
);

const SelectAccountModal = ({ brand, path, loadSocials = () => null }) => {
  const router = useHistory();
  const { search, pathname } = useLocation();
  const platform = new URLSearchParams(search).get("platform");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState();
  const [selectedAccountProfilePic, setSelectedAccountProfilePic] =
    useState("");
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(null);
  const [nextPage, setNextPage] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const getAccounts = useCallback(async () => {
    if (!brand?.id) return;
    setFetchingAccounts(true);
    try {
      let res = await axiosInstance.get(
        `${platform}/${brand?.id}/pages/get?count=15${
          nextPage ? `&next=${nextPage}` : ""
        }`
      );
      if (res.data.status === false) {
        showAlert({
          msg: res.data.message,
          type: "danger",
        });
      } else {
        const list = res.data?.data?.pages;
        if (platform === "instagram") {
          setHasMore(res.data?.data?.next !== null ? true : false);
          setPage(res.data?.data?.next);
        } else {
          setHasMore(res.data?.data?.pagination?.next !== null ? true : false);
          setPage(res.data?.data?.pagination?.next);
        }

        list &&
          setAccounts((prev) => {
            return [...new Set([...prev, ...list])];
          });
        list?.forEach((account) => {
          if (account.selected) {
            setSelectedAccountId(
              platform === "instagram" ? account.ig_id : account.id
            );
          }
        });
      }
      setFetchingAccounts(false);
    } catch (e) {
      showAlert({
        msg: "Error. Could not get accounts",
        type: "danger",
      });
      setFetchingAccounts(false);
      console.log(e);
    }
  }, [brand, nextPage, platform]);

  const handleSelectAccount = (accountId, profilePic) => {
    if (!accountId) return;
    setSelectedAccountId(accountId);
    setSelectedAccountProfilePic(profilePic);
  };

  const handleSubmitAccount = useCallback(
    async (e) => {
      e.preventDefault();

      let data = [];

      data = accounts?.filter((item) => {
        if (item.ig_id) {
          return item.ig_id === selectedAccountId;
        }
        return item.id === selectedAccountId;
      });

      if (platform === "facebook" && !!data.length) {
        data[0].profile_picture = selectedAccountProfilePic;
      }

      if (data?.length > 0) {
        setSubmitting(true);

        try {
          let res = await axiosInstance.post(
            `${platform}/${brand?.id}/page/store`,
            { accounts: data }
          );
          if (res.data.status === false) {
            showAlert({
              msg: res.data.message ?? "An error occurred.",
              type: "danger",
            });
          } else {
            pathname.includes("posts")
              ? router.push(
                  `/dashboard/posts/calendar-view?post_action=create_post&success=${res.data.message}&connected_from_post=true`
                )
              : router.push(
                  `/dashboard/brands/${brand.id}/edit/channels?success=${res.data.message}`
                );
            await loadSocials();
          }
          setSubmitting(false);
        } catch (e) {
          setSubmitting(false);
          showAlert({
            msg: e.response.data.message || "An error occurred. Try again",
            type: "danger",
          });
        }
      } else {
        showAlert({
          msg: "You must make at least one selection",
          type: "danger",
        });
      }

      data = [];
    },
    // eslint-disable-next-line
    [platform, accounts, selectedAccountId]
  );

  const observer = useRef();
  const lastListElementRef = useCallback(
    (node) => {
      if (fetchingAccounts) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setNextPage(page);
        }
      });
      if (node) observer.current.observe(node);
    },
    // eslint-disable-next-line
    [fetchingAccounts, hasMore]
  );

  useEffect(() => {
    if (brand) {
      getAccounts();
    }
    // eslint-disable-next-line
  }, [brand, nextPage]);

  return (
    <SelectModal className="selectModal show">
      <form className="card" onSubmit={(e) => handleSubmitAccount(e)}>
        <div className="cardBody">
          <h5>Which channel would you like to add?</h5>
          <Spacer y={0.6} />
          <p className="description colorBodyText">
            {platform === "linkedin"
              ? "Add Your LinkedIn Profile or Business Page"
              : "Select one social media channel you want to add to PostPaddy."}
          </p>
          <Spacer y={2.4} />
          {!fetchingAccounts && !accounts.length && (
            <p className="l3 colorGrey1">No channels available.</p>
          )}

          <div className="block">
            {platform === "instagram" &&
              accounts.length >= 1 &&
              accounts.map((item, index) => (
                <>
                  <div
                    key={index}
                    className={`flexRow alignCenter chooseTargetItem ${
                      item.exists && "disabled"
                    }`}
                    ref={
                      accounts?.length === index + 1 ? lastListElementRef : null
                    }
                  >
                    <RadioInput
                      id={`_${item.ig_id}`}
                      name="instagram_account"
                      value={item.ig_id}
                      checked={selectedAccountId === item.ig_id}
                      onClick={() => handleSelectAccount(item.ig_id)}
                      disabled={item.exists}
                    />
                    <Spacer x={1.2} />
                    <label
                      htmlFor={`_${item.ig_id}`}
                      className="flexRow alignCenter"
                    >
                      <div className="user-image-container">
                        <ImgWrapper size={4.0} imgHeight={"100%"}>
                          <img
                            src={item.profile_picture || userIcon}
                            alt="avi"
                          />
                        </ImgWrapper>
                        <div className="social-icon flexRow alignCenter justifyCenter">
                          <img
                            src={socialIcons.circle["instagram"]}
                            alt="text"
                            width="16px"
                          />
                        </div>
                      </div>
                      <Spacer x={1.2} />
                      <span className="text l3 colorBodyText">
                        {item.username}
                      </span>
                    </label>
                  </div>
                  {item?.exists && (
                    <Info
                      content={`Profile connected to ${item.owner} workspace on PostPaddy`}
                    />
                  )}
                </>
              ))}
            {platform === "facebook" &&
              accounts.length >= 1 &&
              accounts.map((item, index) => {
                return (
                  <FacebookContainer
                    item={item}
                    key={index}
                    selectedAccountId={selectedAccountId}
                    handleSelectAccount={handleSelectAccount}
                    ref={
                      accounts?.length === index + 1 ? lastListElementRef : null
                    }
                  />
                );
              })}
            {platform === "linkedin" && (
              <>
                <p className="colorGrey1 body-2 bold">LinkedIn profile</p>
                <Spacer y={1.2} />
                {accounts.length >= 1 &&
                  accounts
                    .filter((item) => item.category === "personal")
                    .map((item, index) => (
                      <>
                        <div
                          key={index}
                          className={`flexRow alignCenter chooseTargetItem ${
                            item.exists && "disabled"
                          }`}
                        >
                          <RadioInput
                            id={`_${item.id}`}
                            name="linkedin_account"
                            value={item.id}
                            checked={selectedAccountId === item.id}
                            onClick={() => handleSelectAccount(item.id)}
                            disabled={item.exists}
                          />
                          <Spacer x={1.2} />
                          <label
                            htmlFor={`_${item.id}`}
                            className="flexRow alignCenter"
                          >
                            <div className="user-image-container">
                              <ImgWrapper size={4.0} imgHeight={"100%"}>
                                <img
                                  src={item.profile_picture || userIcon}
                                  alt="avi"
                                />
                              </ImgWrapper>
                              <div className="social-icon flexRow alignCenter justifyCenter">
                                <img
                                  src={socialIcons.circle["linkedin"]}
                                  alt="text"
                                  width="16px"
                                />
                              </div>
                            </div>
                            <Spacer x={1.2} />
                            <span className="text l3 colorBodyText">
                              {item.name}
                            </span>
                          </label>
                        </div>
                        {item?.exists && (
                          <Info
                            content={`Profile connected to ${item.owner} workspace on PostPaddy`}
                          />
                        )}
                      </>
                    ))}
                <div className="divider"></div>
                <p className="colorGrey1 body-2 bold">LinkedIn page</p>
                <Spacer y={1.2} />
                {accounts.length >= 1 &&
                  accounts
                    .filter((item) => item.category === "company")
                    .map((item, index) => (
                      <>
                        <div
                          key={index}
                          className={`flexRow alignCenter chooseTargetItem ${
                            item.exists && "disabled"
                          }`}
                          ref={
                            accounts?.length === index + 1
                              ? lastListElementRef
                              : null
                          }
                        >
                          <RadioInput
                            id={`_${item.id}`}
                            name="linkedin_account"
                            value={item.id}
                            checked={selectedAccountId === item.id}
                            onClick={() => handleSelectAccount(item.id)}
                            disabled={item.exists}
                          />
                          <Spacer x={1.2} />
                          <label
                            htmlFor={`_${item.id}`}
                            className="flexRow alignCenter"
                          >
                            <div className="user-image-container">
                              <ImgWrapper size={4.0} imgHeight={"100%"}>
                                <img
                                  src={item.profile_picture || userIcon}
                                  alt="avi"
                                />
                              </ImgWrapper>
                              <div className="social-icon flexRow alignCenter justifyCenter">
                                <img
                                  src={socialIcons.circle["linkedin"]}
                                  alt="text"
                                  width="16px"
                                />
                              </div>
                            </div>
                            <Spacer x={1.2} />
                            <span className="text l3 colorBodyText">
                              {item.name}
                            </span>
                          </label>
                        </div>
                        {item?.exists && (
                          <Info
                            content={`Page connected to ${item.owner} workspace on PostPaddy`}
                          />
                        )}
                      </>
                    ))}
              </>
            )}
            {fetchingAccounts && (
              <div className="textCenter">
                <Loader type="spinner" />
              </div>
            )}
          </div>
        </div>
        <Spacer y={2.4} />
        <div className="flexRow justifySpaceBetween actionBtns">
          <button
            type="button"
            className="btn cancel flexRow alignCenter justifyCenter"
            onClick={() => {
              router.push(path);
              document.querySelector(".selectModal").classList.remove("show");
            }}
          >
            Cancel
          </button>
          <Spacer x={2.4} />
          <button
            type="submit"
            className="btn actionBtn flexRow alignCenter justifyCenter"
            disabled={submitting || !selectedAccountId}
          >
            {submitting ? <Loader type="spinner" /> : "Add"}
          </button>
        </div>
      </form>
    </SelectModal>
  );
};

export default SelectAccountModal;
